import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface AlreadyCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestart: () => void;
}

export default function AlreadyCreatedModal({
  isOpen,
  onClose,
  onRestart
}: AlreadyCreatedModalProps) {

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] overflow-y-auto"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        backgroundColor: 'rgba(0, 0, 0, 0.6)'
      }}
      aria-labelledby="modal-title" 
      role="dialog" 
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Hintergrund-Overlay */}
        <div 
          className="fixed inset-0 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }}
        />

        {/* Zentrierungs-Trick */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal Panel */}
        <div 
          className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          style={{
            backgroundColor: 'white',
            border: '3px solid #f59e0b',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            position: 'relative',
            zIndex: 1000000
          }}
        >
          {/* Header */}
          <div className="bg-orange-500 px-8 py-8">
            <div className="flex items-center justify-center">
              <ExclamationTriangleIcon className="h-16 w-16 text-white mr-4" />
              <h3 className="text-3xl font-bold text-white">
                Angebot bereits erstellt!
              </h3>
            </div>
          </div>
          
          {/* Content */}
          <div className="bg-white px-8 py-8">
            <div className="text-center space-y-6">
              {/* Hauptnachricht */}
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
                <p className="text-xl font-bold text-orange-800 mb-3">
                  ⚠️ Sie haben bereits ein Angebot erhalten!
                </p>
                <p className="text-lg text-orange-700">
                  Um doppelte Angebote zu vermeiden, können Sie nur einmal pro Konfiguration ein Angebot erstellen.
                </p>
              </div>

              {/* Optionen */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <p className="text-xl font-bold text-blue-900 mb-4">Was möchten Sie tun?</p>
                <ul className="text-left space-y-3 text-lg text-blue-800">
                  <li className="flex items-center">
                    <span className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center mr-3 font-bold">1</span>
                    <strong>Neue Konfiguration starten</strong> (empfohlen)
                  </li>
                  <li className="flex items-center">
                    <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3 font-bold">2</span>
                    Auf Ihr bestehendes Angebot warten
                  </li>
                  <li className="flex items-center">
                    <span className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center mr-3 font-bold">3</span>
                    Bei Fragen unser Vertriebsteam kontaktieren
                  </li>
                </ul>
              </div>

              {/* Call-to-Action */}
              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                <p className="text-lg font-bold text-green-800 mb-3">
                  💡 Tipp: Starten Sie eine neue Konfiguration!
                </p>
                <p className="text-base text-green-700">
                  So können Sie verschiedene Varianten vergleichen und das beste Angebot für Ihre Bedürfnisse finden.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-6">
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={onRestart}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-bold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg"
              >
                <ArrowPathIcon className="h-6 w-6 mr-2" />
                Neue Konfiguration starten
              </button>
              
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-6 py-4 border border-gray-300 text-lg font-bold rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-md"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
